import { Link, useNavigate } from "react-router";
import { ENTRY_OPENS_STUDENT_MIN, type ScheduleEntry } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { timeText } from "./common.ts";

/**
 * Главное действие карточки занятия.
 *
 * Своим файлом с 03.09.2026: карточка упёрлась в предел в 400 строк (раздел 9
 * CLAUDE.md), а кнопка это связный кусок с собственным правилом, у которого
 * шесть веток и на каждой висит свой разбор. Граница та же, что у полосы
 * отметок и у двери в домашку: карточка ОПИСЫВАЕТ занятие, а этот файл
 * отвечает на вопрос «что с ним сделать сейчас».
 *
 * ⚠️ **Акцентной кнопки здесь нет НИ ОДНОЙ, и это правило, а не пропуск.**
 * Разбор целиком в шапке `EntryCard.tsx`: занятий на экране десятки, и
 * двенадцать залитых «Войти» это ноль выделенных кнопок.
 */

/**
 * ⚠️ Урок создаётся ТОЛЬКО по нажатию, и других путей нет: автоматического
 * создания по часам в проекте не будет, оно означало бы фоновую задачу,
 * плодящую уроки для занятий, куда никто не придёт.
 *
 * Порядок веток и есть смысл кнопки. Проведённое ведёт в разбор, живое и
 * подготовленное в комнату, а занятие без урока предлагает его завести, и
 * подпись у этого разная: преподаватель готовит заранее, ученик входит.
 */
export function EntryAction({
  entry,
  zone,
  busy,
  run,
}: {
  entry: ScheduleEntry;
  /** Зона СМОТРЯЩЕГО: во сколько откроется дверь, ученик обязан прочитать в своих часах. */
  zone: string;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  const navigate = useNavigate();

  /**
   * ⚠️ **У занятия без НАШЕЙ комнаты главного действия нет ни у кого**, и
   * проверка стоит первой веткой (правка по итогам ревью 29.08.2026). Это
   * занятие ГРУППЫ у преподавателя, которого нет в списке комнаты: ручка входа
   * отвечает ему `409 group_room_unavailable`, то есть кнопка «Войти» вела бы в
   * отказ, а строка «вход с 15:55» обещала бы ученику дверь, которой нет.
   *
   * Слов на её месте тоже нет: занятие идёт по внешней ссылке, а ссылка стоит
   * строкой выше, и указывать на неё словами значит показывать пальцем на
   * соседний элемент.
   *
   * Признак считает СЕРВЕР и присылает готовым (`roomAvailable`): условие
   * «есть ли у занятия комната» обязано жить в одном месте с отказом, иначе
   * кнопка и сервер разойдутся.
   *
   * ⚠️ **У преподавателя ИЗ списка сюда приезжает `roomAvailable: true` при
   * пустом `lessonId`** (условие 6 рубежа включения, `docs/group-room-design.md`,
   * раздел 17), и это ровно тот случай, ради которого ветка написана парой, а не
   * одним полем: управление идёт дальше и рисует обычное действие входа.
   * `lessonId` сводной строке кладут отдельной работой, ПОСЛЕ `archivable`
   * (раздел 14 проекта): наоборот, и прошлое занятие нарисует «Открыть» вместо
   * «Разбор», уведя преподавателя в живую комнату опорного урока.
   *
   * ⚠️ **Вместе с этим стал достижим и промах `EntryKind.tsx`**: вид занятия он
   * читает по наличию поля `group`, которого у осиротевшего урока нет никогда, и
   * пока уроков у занятия группы не заводилось, это было безопасно. Разбор там
   * же, чинится признаком вида полем.
   */
  if (!entry.roomAvailable && entry.lessonId === null) return null;

  /**
   * ⚠️ Ученику до окна входа кнопки нет ВООБЩЕ, даже если урок уже подготовлен.
   * Его окно открывается за пять минут до начала (либо раньше, если
   * преподаватель уже в комнате), а преподавателю за пятнадцать, и эти минуты
   * не привилегия: у урока, заведённого учеником, заготовка осталась бы пустой.
   *
   * ⚠️ Держится это на состоянии, которое считает СЕРВЕР, и до 15.08.2026 не
   * держалось. Подготовленный урок приезжал ученику состоянием `ready` в любой
   * час, потому что ветка «урок есть» стояла раньше всякой проверки часов, и
   * кнопка «Открыть» была тут за неделю до занятия. Комментарий на этом месте
   * обещал обратное, но опирался на `scheduled`, до которого дело не доходило.
   * Теперь состояние честное, а сервер отказывает `409 entry_not_open` и без
   * всякой кнопки: адрес урока можно набрать руками.
   */
  if (entry.myRole === "student" && entry.state === "scheduled") {
    /*
      Не «ещё не началось», а во сколько откроется дверь. Кнопки тут нет
      намеренно (сервер ответил бы `409 entry_not_open`), но человеку, который
      пришёл рано, нужен не запрет, а время: без него он обновляет страницу
      наугад либо пишет преподавателю ровно тогда, когда тот готовится.

      ⚠️ Время считается ОТ момента занятия, который прислал сервер, а не от
      часов устройства: они бывают сбиты на десятки минут. Само решение
      «рано или нет» принято сервером и здесь не пересчитывается.
    */
    return (
      <ActionSlot>
        <span className="muted">
          вход с {timeText(entry.at - ENTRY_OPENS_STUDENT_MIN * 60_000, zone)}
        </span>
      </ActionSlot>
    );
  }

  if (entry.lessonId !== null) {
    if (entry.archivable) {
      return (
        <ActionSlot>
          <Link className="btn" to={`/archive/${entry.lessonId}`}>
            Разбор
          </Link>
        </ActionSlot>
      );
    }
    return (
      <ActionSlot>
        <Link className="btn" to={`/lesson/${entry.lessonId}`}>
          {entry.state === "live" ? "Вернуться" : "Открыть"}
        </Link>
      </ActionSlot>
    );
  }

  // Строка второго потока и заглушка перенесённого занятия кнопки не несут:
  // у первой урок уже открыт веткой выше, у второй занятие живёт на новом месте
  if (entry.slotId === null || entry.occursOn === null) return null;
  if (entry.state === "cancelled" || entry.state === "moved_away") return null;

  /**
   * ⚠️ У `missed` кнопки нет НИ У КОГО, и до 15.08.2026 она тут была.
   * `missed` без урока означает `now >= closesAt`, а ручка входа отвечает на
   * закрытое окно `409 entry_closed` первой же проверкой, то есть кнопка
   * «Создать урок» отвечала отказом всегда и обеим сторонам. Занятие при этом
   * не потеряно: преподаватель переносит его формой ниже, и вход открывается
   * заново на новом месте.
   */
  if (entry.state === "missed") {
    return (
      <ActionSlot>
        <span className="muted">окно входа закрылось</span>
      </ActionSlot>
    );
  }

  const slotId = entry.slotId;
  const occursOn = entry.occursOn;

  /**
   * Подпись говорит, что произойдёт, а не как называется состояние.
   * «Подготовить» это завести урок заранее и разложить материал, «Войти» это
   * начать занятие сейчас.
   */
  const label = entry.state === "scheduled" ? "Подготовить" : "Войти";

  return (
    <ActionSlot>
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            const lesson = await api.enterOccurrence(slotId, occursOn);
            void navigate(`/lesson/${lesson.id}`);
          })
        }
      >
        {busy ? "…" : label}
      </button>
    </ActionSlot>
  );
}

/**
 * Место главного действия в ряду: правый край, после значков-инструментов.
 *
 * ⚠️ **Коробку рисует САМО действие, а не карточка вокруг него**, и это не
 * раскладка, а признак. Ряд действий бывает пуст целиком (ученик у отменённого
 * занятия: инструментов ему нет, действия тоже), и пустая коробка добавляла бы
 * карточке лишний отступ сеткой `.card`. Пока эту коробку рисовала карточка,
 * ряд НИКОГДА не был пуст в разметке (в нём всегда лежал пустой `<span>`), и
 * прятать его приходилось селектором `:has()`, которого нет в цели сборки
 * (Firefox 115, `:has()` приехал в 121): правило с ним браузер выбрасывает
 * целиком, то есть отступ оставался. Теперь пустой ряд пуст и в разметке, и
 * прячет его `.wk__tools:empty`, то есть правило из тех же лет, что сама
 * вёрстка.
 *
 * Второй довод тот же, что был у переезда самого действия в свой файл: кто
 * решает, показывать ли действие, тот и говорит, есть ли оно. Признак, который
 * считала бы карточка, означал бы копию всех шести веток выше, и разъехалась бы
 * она молча.
 */
function ActionSlot({ children }: { children: React.ReactNode }) {
  return <span className="wk__tools-main">{children}</span>;
}
